import { useEffect, useMemo, useState } from 'react';
import type { DeliveryConditionTierDto, UpdateDeliveryConditionTierModel } from 'api/model';
import {
  useGlobalSettingsGetDeliveryConditionTiers,
  useGlobalSettingsUpdateDeliveryConditionTier,
} from 'api/global-settings/global-settings';
import useMe from 'contexts/authentication/useMe';
import Roles, { includesRoles } from 'constants/Roles';
import styled from 'styled-components';
import { faSave, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { showException } from 'utils/exception-helper';
import Table from 'components/Table';
import { ColumnSetting } from 'components/Table/utils';
import Input from 'components/inputs/Input';
import TextButton from 'components/inputs/TextButton';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const CellRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

interface TierRow {
  id: number;
  name: string;
  minRating: number;
  maxRating: number;
  requiresFollowUp: boolean;
  requiresSellerContact: boolean;
  sortOrder: number;
}

interface PendingEdit {
  name?: string;
  minRating?: number;
  maxRating?: number;
}

const DeliveryConditionTierSettings = () => {
  const me = useMe();
  const isSuperAdmin = includesRoles([Roles.SuperAdmin], me?.roles);
  const { data: tiers, isLoading, refetch } =
    useGlobalSettingsGetDeliveryConditionTiers();
  const updateCall = useGlobalSettingsUpdateDeliveryConditionTier();

  const [edits, setEdits] = useState<Record<number, PendingEdit>>({});
  const [savingRows, setSavingRows] = useState<Set<number>>(new Set());

  useEffect(() => {
    setEdits({});
  }, [tiers]);

  const rows = useMemo((): TierRow[] => {
    return (tiers ?? [])
      .slice()
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map((t: DeliveryConditionTierDto) => ({
        id: t.id,
        name: t.name,
        minRating: t.minRating,
        maxRating: t.maxRating,
        requiresFollowUp: t.requiresFollowUp,
        requiresSellerContact: t.requiresSellerContact,
        sortOrder: t.sortOrder,
      }));
  }, [tiers]);

  const getDisplay = (row: TierRow) => ({
    name: edits[row.id]?.name ?? row.name,
    minRating: edits[row.id]?.minRating ?? row.minRating,
    maxRating: edits[row.id]?.maxRating ?? row.maxRating,
  });

  const isChanged = (row: TierRow) => {
    const d = getDisplay(row);
    return (
      d.name !== row.name ||
      d.minRating !== row.minRating ||
      d.maxRating !== row.maxRating
    );
  };

  const setField = (id: number, field: keyof PendingEdit, value: string | number) => {
    setEdits((prev) => ({
      ...prev,
      [id]: { ...prev[id], [field]: value },
    }));
  };

  const handleSave = async (row: TierRow) => {
    const d = getDisplay(row);
    if (
      d.minRating < 0 ||
      d.maxRating > 10 ||
      d.minRating > d.maxRating ||
      !d.name.trim()
    ) {
      showException(new Error('Min/Max måste vara 0-10 och namnet får inte vara tomt.'));
      return;
    }
    const data: UpdateDeliveryConditionTierModel = {
      tierID: row.id,
      minRating: d.minRating,
      maxRating: d.maxRating,
      name: d.name.trim(),
    };
    setSavingRows((prev) => new Set(prev).add(row.id));
    try {
      await updateCall.mutateAsync({ data });
      refetch();
    } catch (error) {
      showException(error);
    } finally {
      setSavingRows((prev) => {
        const next = new Set(prev);
        next.delete(row.id);
        return next;
      });
    }
  };

  const columnSettings = useMemo(
    (): ColumnSetting<TierRow, object>[] => [
      {
        head: 'Ordning',
        width: 80,
        render: (row) => <span>{row.sortOrder}</span>,
      },
      {
        head: 'Namn',
        width: 260,
        render: (row) => (
          <Input
            type="text"
            small
            width={240}
            disabled={!isSuperAdmin}
            value={getDisplay(row).name}
            onChange={(e) => setField(row.id, 'name', e.target.value)}
          />
        ),
      },
      {
        head: 'Min',
        width: 100,
        render: (row) => (
          <Input
            type="number"
            min={0}
            max={10}
            small
            width={70}
            disabled={!isSuperAdmin}
            value={getDisplay(row).minRating}
            onChange={(e) => setField(row.id, 'minRating', Number(e.target.value))}
          />
        ),
      },
      {
        head: 'Max',
        width: 100,
        render: (row) => (
          <Input
            type="number"
            min={0}
            max={10}
            small
            width={70}
            disabled={!isSuperAdmin}
            value={getDisplay(row).maxRating}
            onChange={(e) => setField(row.id, 'maxRating', Number(e.target.value))}
          />
        ),
      },
      {
        head: 'Kräver bild/kommentar',
        width: 180,
        render: (row) => <span>{row.requiresFollowUp ? 'Ja' : 'Nej'}</span>,
      },
      {
        head: 'Mail kvalitet + säljarsvar',
        width: 220,
        render: (row) => <span>{row.requiresSellerContact ? 'Ja' : 'Nej'}</span>,
      },
      ...(isSuperAdmin
        ? [
            {
              head: '',
              width: 50,
              render: (row: TierRow) => (
                <CellRow>
                  {savingRows.has(row.id) ? (
                    <FontAwesomeIcon icon={faSpinner} spin />
                  ) : isChanged(row) ? (
                    <TextButton onClick={() => handleSave(row)}>
                      <FontAwesomeIcon icon={faSave} />
                    </TextButton>
                  ) : null}
                </CellRow>
              ),
            },
          ]
        : []),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rows, edits, savingRows, isSuperAdmin]
  );

  if (isLoading) {
    return <FontAwesomeIcon icon={faSpinner} spin />;
  }

  return (
    <Wrapper>
      <h1>
        Leveransskick — nivåer{' '}
        {isLoading && <FontAwesomeIcon icon={faSpinner} spin />}
      </h1>
      <p>
        Konfigurera namn och betygsintervall för de tre nivåerna av
        leveransskick. Intervallen får inte överlappa.
      </p>
      <Table fluid columnSettings={columnSettings} rows={rows} />
    </Wrapper>
  );
};

export default DeliveryConditionTierSettings;
